import { ReactNode, useEffect, useRef, useState } from "react";
import { useController, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";

import { BidStageSelect } from "components/ui/form_components/StageSelect/BidStageSelect";
import { EventNames, useTracking } from "lib/tracking";
import { CustomOrganisationPrimaryRole } from "lib/types/graphQLEnums";
import { BidDetails } from "../../../lib/hooks/api/bid/bids/useBid";
import { useCreateCustomBidOrganisation } from "../../../lib/hooks/api/bid/bids/useCreateCustomBidOrganisation";
import { useFormAutoSubmit } from "../../../lib/hooks/useFormAutoSubmit";
import { NoticesStagesFilter } from "../../../lib/types/graphQLEnums";
import { Flex, Text } from "../../../styles/utility-components";
import { SavedNoticeCard } from "../../entities/save_components/SaveNoticeEntitiesCard";
import Button from "../../ui/button/Button";
import { BuyersCombobox } from "../../ui/form_components/BidEntityCombobox/BuyersCombobox";
import { FrameworkCombobox } from "../../ui/form_components/BidEntityCombobox/FrameworkCombobox";
import { RelatedLeadCombobox } from "../../ui/form_components/BidEntityCombobox/RelatedLeadCombobox";
import { SuppliersCombobox } from "../../ui/form_components/BidEntityCombobox/SuppliersCombobox";
import { CompanyUserCombobox } from "../../ui/form_components/CompanyUserCombobox";
import { TenderCombobox } from "../../ui/form_components/TenderCombobox";
import DateInput from "../../ui/input/DateInput";
import InputNumber from "../../ui/input/InputNumber";
import toast from "../../ui/toast/Toast";
import {
  BidUpdateFormInput,
  BidUpdateFormOutput,
  updateBidSchema,
  useBidUpdateFormSubmit,
} from "./useBidUpdateFormSubmit";

type Props = {
  bid: BidDetails;
};

export default function BidDetailsSidebar({ bid }: Props) {
  const { logEvent } = useTracking();
  const form = useForm<BidUpdateFormInput, unknown, BidUpdateFormOutput>({
    defaultValues: {
      name: bid.name,
      value: bid.value,
      description: bid.description,
      assignedToId: bid.assignedTo?.guid,
      stageId: bid.stage.id,
      clarificationDeadline: bid.clarificationDeadline,
      submissionDate: bid.submissionDate,
      startDate: bid.startDate,
      awardDate: bid.awardDate,
      buyerIds: [...bid.buyers.map((b) => b.id), ...bid.customBuyers.map((b) => b.id)],
      supplierIds: bid.suppliers.map((s) => s.id),
      noticeProcStageId: bid.notice?.procurementStage.id ?? null,
      frameworkId: bid.framework ? bid.framework.id : null,
      leadId: bid.lead?.id ?? null,
    },
    resolver: zodResolver(updateBidSchema),
    mode: "onChange",
  });

  const { debouncedHandleSubmit, submitValueUpdate } = useBidUpdateFormSubmit({ bid, form });

  const { mutate: createCustomOrganisation } = useCreateCustomBidOrganisation({
    onSuccess: () => {
      toast.success(`Custom organisation saved to ${bid.name}`);
      logEvent(EventNames.bidDataAdded, {
        "Bid ID": bid.id,
        "Free-text buyer": true,
        "Amount of entities": 1,
      });
    },
    onError: (error) => {
      console.warn("Failed to create custom organisation on bid", JSON.stringify(error));
      toast.error("Failed to create custom organisation");
    },
  });

  const { control, watch, getValues, trigger, setValue } = form;

  // Keep entity field values in sync with bid data from the server.
  // This handles cases like the UNDO toast re-adding an entity, which updates
  // `bid` via query invalidation but does not update the form's controlled value.
  useEffect(() => {
    setValue("buyerIds", [...bid.buyers.map((b) => b.id), ...bid.customBuyers.map((b) => b.id)]);
  }, [bid.buyers, bid.customBuyers, setValue]);

  useEffect(() => {
    setValue(
      "supplierIds",
      bid.suppliers.map((s) => s.id),
    );
  }, [bid.suppliers, setValue]);

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: debouncedHandleSubmit,
    provideMetadata: true,
  });

  const {
    field: { onChange: updateRelatedTender },
  } = useController({
    control,
    name: "noticeProcStageId",
  });

  return (
    <Form>
      <Section>
        <Text h3>Tender</Text>
        <Property aria-label="Notice field">
          {bid.notice ? (
            <SavedNoticeCard
              notice={{
                ...bid.notice,
                name: bid.notice.name ?? "Untitled Notice",
                stagev2: bid.notice.stagev2 ?? null,
                expiryDate: bid.notice.expiryDate ?? null,
                closeDate: bid.notice.closeDate ?? null,
                publishDate: bid.notice.publishDate ?? null,
                procurementStageId: bid.notice.procurementStage.id,
              }}
              contextSource={"Bid details"}
              removeEntity={() => updateRelatedTender(null)}
            />
          ) : (
            <TenderCombobox
              control={control}
              name="noticeProcStageId"
              defaultNoticeRequest={{
                stages: [NoticesStagesFilter.Open, NoticesStagesFilter.OpenTender],
                excludeNoticesSavedToBids: true,
              }}
            />
          )}
        </Property>
      </Section>
      {/* Details Section */}
      <Section>
        <Text h3>Details</Text>
        <Property aria-label="Stage field">
          <FieldLabel>Stage</FieldLabel>
          <FieldValue>
            <BidStageSelect control={control} name="stageId" aria-label="Stage search" />
          </FieldValue>
        </Property>

        <Property aria-label="Owner field">
          <FieldLabel>Owner</FieldLabel>
          <FieldValue>
            <CompanyUserCombobox control={control} name="assignedToId" placeholder="Select user" />
          </FieldValue>
        </Property>

        <Property aria-label="Buyer field">
          <FieldLabel>Buyer</FieldLabel>
          <FieldValue>
            <BuyersCombobox
              control={control}
              name="buyerIds"
              customBuyers={bid.customBuyers}
              onAddCustomBuyer={(name: string) =>
                createCustomOrganisation({
                  bidId: bid.id,
                  input: { name, primaryRole: CustomOrganisationPrimaryRole.Buyer },
                })
              }
            />
          </FieldValue>
        </Property>

        <Property aria-label="Supplier field">
          <FieldLabel>Supplier</FieldLabel>
          <FieldValue>
            <SuppliersCombobox control={control} name="supplierIds" />
          </FieldValue>
        </Property>

        <Property aria-label="Value field">
          <FieldLabel>Value</FieldLabel>
          <InlineSelectWrapper
            displayValue={
              bid.value != null ? (
                <FieldTextValue>£{bid.value.toLocaleString()}</FieldTextValue>
              ) : undefined
            }
          >
            <InputNumber
              name="value"
              autoFocus // Input only renders when you click edit + it's needed to ensure onBlur triggers on click away
              control={control}
              placeholder="£100,000"
              onBlur={submitValueUpdate}
              onKeyDown={(e) => {
                // Enter commits the value: blur triggers save + exits edit mode
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
            />
          </InlineSelectWrapper>
        </Property>

        <Property aria-label="Framework field">
          <FieldLabel>Framework</FieldLabel>
          <FieldValue>
            <FrameworkCombobox name="frameworkId" control={control} />
          </FieldValue>
        </Property>
      </Section>

      {/* Key dates Section */}
      <Section>
        <Text h3>Key dates</Text>
        <Property aria-label="Clarification deadline field">
          <FieldLabel>Clarification deadline</FieldLabel>
          <DateInput
            name="clarificationDeadline"
            control={control}
            calendarProps={{ mode: "single" }}
            triggerWidth="100%"
          />
        </Property>

        <Property aria-label="Submission date field">
          <FieldLabel>Submission date</FieldLabel>
          <DateInput
            name="submissionDate"
            control={control}
            calendarProps={{ mode: "single" }}
            triggerWidth="100%"
          />
        </Property>

        <Property aria-label="Start date field">
          <FieldLabel>Start date</FieldLabel>
          <DateInput
            name="startDate"
            control={control}
            calendarProps={{ mode: "single" }}
            triggerWidth="100%"
          />
        </Property>

        <Property aria-label="Award date field">
          <FieldLabel>Award date</FieldLabel>
          <DateInput
            name="awardDate"
            control={control}
            calendarProps={{ mode: "single" }}
            triggerWidth="100%"
          />
        </Property>
      </Section>

      {/* Related lead Section */}
      <Section>
        <Text h3>Related lead</Text>
        <Property aria-label="Related Lead field">
          <FieldLabel>Related Lead</FieldLabel>
          <RelatedLeadCombobox
            control={control}
            name="leadId"
            defaultLeadRequest={{
              assignedToBid: false,
            }}
            selectedLead={bid.lead}
          />
        </Property>
      </Section>
    </Form>
  );
}

type InlineSelectWrapperProps = {
  children: ReactNode;
  displayValue: ReactNode | undefined;
};

function InlineSelectWrapper({ children, displayValue }: InlineSelectWrapperProps) {
  const [isEditing, setIsEditing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const clearBlurTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    clearBlurTimeout();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      timeoutRef.current = setTimeout(() => {
        setIsEditing(false);
        timeoutRef.current = null;
      }, 500);
    }
  };

  const handleFocus = () => {
    clearBlurTimeout();
  };

  if (isEditing) {
    return (
      <FieldValue onBlur={handleBlur} onFocus={handleFocus}>
        {children}
      </FieldValue>
    );
  }

  if (displayValue) {
    return (
      <DisplayContainer aria-label="Edit field" onClick={() => setIsEditing(true)}>
        {displayValue}
      </DisplayContainer>
    );
  }

  return (
    <ClickToAddValueContainer>
      <Button
        buttonStyle="inverse"
        iconProps={{ color: "sysTextPlaceholder" }}
        buttonType="primary"
        icon="plus"
        onClick={() => setIsEditing(true)}
        fullWidth
        justifyContent="flex-start"
      >
        <Text color="sysTextPlaceholder">Click to add</Text>
      </Button>
    </ClickToAddValueContainer>
  );
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: 48,
  padding: 24,
});

const Section = styled(Flex)({
  flexDirection: "column",
  gap: 16,
});

const Property = styled(Flex)({
  alignItems: "center",
  gap: 8,
});

const FieldLabel = styled.label(({ theme }) => ({
  width: 130,
  color: theme.colors.sysTextSecondary,
  flexShrink: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  lineClamp: "1",
  WebkitLineClamp: 1,
  WebkitBoxOrient: "vertical",
  wordBreak: "break-all",
  alignSelf: "flex-start",
  padding: "5px 0",
}));

const FieldValue = styled.div<{ minWidth?: string | number; maxWidth?: string | number }>(
  ({ minWidth = 250, maxWidth = 400 }) => ({
    flex: 1,
    minWidth,
    maxWidth,
  }),
);

const ClickToAddValueContainer = styled(Flex)({
  flex: 1,
  minWidth: 250,
  maxWidth: 350,
});

const FieldTextValue = styled(Text)({
  maxWidth: 300,
});

const DisplayContainer = styled(Flex)(({ theme }) => ({
  flexWrap: "wrap",
  alignItems: "center",
  flex: 1,
  gap: 8,
  padding: "5px 12px", // 12px left aligns the resting value with the date input below
  borderRadius: 4,
  cursor: "pointer",
  maxWidth: 400,
  "&:hover": {
    backgroundColor: theme.colors.sysBackgroundAlternative,
  },
}));
